import { ensureIntersectionObserver } from '@core/polyfills'

import type { BaseElement } from './BaseElement'
import type { Constructor } from './types'

/**
 * Mixin providing onVisibilityChange method.
 *
 * Docs on mixins: https://lit.dev/docs/composition/mixins/
 * @param superClass
 * @returns
 */
export const VisibilityMixin = <T extends Constructor<BaseElement>>(
  superClass: T,
) => {
  class VisibilityMixinClass extends superClass {
    #visibilityObserver: IntersectionObserver | null = null
    #isConnected = false

    /**
     * Root margin for the IntersectionObserver.
     * Override in subclasses to adjust when visibility triggers.
     * Positive values trigger before element enters viewport.
     * @example '200px 0px' - trigger 200px before entering viewport vertically
     */
    protected visibilityRootMargin = '0px'

    /**
     * Initiate and connect IntersectionObserver to the element.
     * The observer is only attached when the `lazy` attribute is set to `"true"`.
     * Otherwise, `onVisibilityChange(true)` is called immediately.
     */
    override connectedCallback() {
      super.connectedCallback()
      this.#isConnected = true

      /**
       * Visibility reporting is opt-in.
       */
      if (this.getAttribute('lazy') !== 'true') {
        this.onVisibilityChange(true)
        return
      }

      ensureIntersectionObserver().then((IO) => {
        if (!this.#isConnected) {
          return
        }

        this.#visibilityObserver = new IO(
          (entries: IntersectionObserverEntry[]) => {
            const entry = entries[0]
            this?.onVisibilityChange(entry.isIntersecting)
          },
          {
            rootMargin: this.visibilityRootMargin,
          },
        )
        this.#visibilityObserver.observe(this)
      })
    }

    /**
     * Disconnect the observer as the element is removed from the DOM.
     */
    override disconnectedCallback() {
      super.disconnectedCallback()
      this.#isConnected = false
      this.#visibilityObserver?.disconnect()
    }

    /**
     * Noop method to be overridden by the component.
     */
    protected onVisibilityChange(_isVisible: boolean) {
      // noop
    }
  }

  return VisibilityMixinClass
}
